import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  CalendarDays, Plus, Trash2, Loader2, AlertTriangle, Search, X, MapPin, Globe2, Info,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { can } from '../lib/permissions';
import {
  listAutonomousCommunities, getAllHolidaysForYear, addHoliday, deleteHoliday,
} from '../services/api';
import { invalidateHolidayCache } from '../lib/spainHolidays';
import { PROVINCES, communityForProvince } from '../lib/spain';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { FEEDBACK } from '../services/feedbackMessages';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const dayOfWeekEs = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`);
  return ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'][d.getDay()];
};

// Los festivos LOCALES se guardan SIEMPRE en su fecha real, aunque caiga en
// domingo: el RD 2001/1983 (art. 46) los publica en su fecha y no se
// trasladan (el traslado al lunes solo existe para ciertas fiestas nacionales,
// art. 45.2, y ya viene en la tabla oficial del BOE). Los que se guardaron
// desplazados antes de este cambio se quedan como están.

const encodeDescription = (description, province, locality) => {
  const desc = (description || '').trim();
  const tags = [province, locality].map((s) => (s || '').trim()).filter(Boolean);
  if (tags.length === 0) return desc;
  return `[${tags.join(' · ')}] ${desc}`.trim();
};

const parseDescription = (raw) => {
  const text = String(raw || '').trim();
  const m = text.match(/^\[([^\]]+)\]\s*(.*)$/);
  if (!m) return { description: text, province: null, locality: null };
  const parts = m[1].split(/[·•|/]/).map((p) => p.trim()).filter(Boolean);
  const [province = null, locality = null] = parts;
  return { description: m[2] || text, province, locality };
};

const normalize = (s) =>
  (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// ─── Modal de alta — orientado a festivos LOCALES (provincia + localidad) ──
const NewHolidayModal = ({ open, communities, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    date: '', description: '', province: '', locality: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const localityInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ date: '', description: '', province: '', locality: '' });
      setApiError(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [open]);

  // Autocomplete de localidad vía Nominatim (OpenStreetMap), filtrado por provincia.
  // Debounce 350ms; resultados deduplicados por nombre de municipio.
  useEffect(() => {
    const q = (form.locality || '').trim();
    if (q.length < 2 || !form.province) {
      setSuggestions([]);
      return;
    }
    const ctrl = new AbortController();
    setLoadingSuggestions(true);
    const t = setTimeout(async () => {
      try {
        // Nominatim NO permite mezclar parámetros structured (country, state)
        // con free-form (q). Embebemos provincia + país en el query libre.
        const fullQuery = `${q}, ${form.province}, España`;
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(fullQuery)}&countrycodes=es&addressdetails=1&limit=15&accept-language=es`;
        const res = await fetch(url, { signal: ctrl.signal, headers: { 'Accept': 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const seen = new Set();
        const out = [];
        const provNorm = normalize(form.province);
        const qNorm = normalize(q);
        for (const item of (Array.isArray(data) ? data : [])) {
          const a = item.address || {};
          const name = a.city || a.town || a.village || a.hamlet || a.municipality || a.suburb || null;
          if (!name) continue;
          // En España el campo `state` de Nominatim es la COMUNIDAD AUTÓNOMA
          // ("Andalucía"), no la provincia. La provincia real está en
          // `address.province` o, si falta, en `address.county`. Comparamos
          // contra cualquiera de los dos (case + acentos insensibles).
          const placeProvinceNorm = normalize(a.province || a.county || '');
          if (placeProvinceNorm && provNorm && placeProvinceNorm !== provNorm) continue;
          const key = normalize(name);
          if (seen.has(key)) continue;
          seen.add(key);
          // Score: prefijos exactos primero, luego "starts with", luego "contains".
          const nameNorm = normalize(name);
          const score = nameNorm === qNorm ? 0 : nameNorm.startsWith(qNorm) ? 1 : 2;
          out.push({ name, province: a.province || a.county || form.province, score });
        }
        out.sort((x, y) => x.score - y.score);
        setSuggestions(out.slice(0, 8));
      } catch {
        // silent
      } finally {
        setLoadingSuggestions(false);
      }
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [form.locality, form.province]);

  // CCAA derivada de la provincia + lookup contra acList del backend.
  // IMPORTANTE: estos hooks deben ejecutarse SIEMPRE en el mismo orden, así
  // que van antes del early return `if (!open) return null` para no violar
  // las Rules of Hooks de React.
  const derivedCommunity = useMemo(() => {
    if (!form.province) return null;
    return communityForProvince(form.province) || null;
  }, [form.province]);

  const derivedCommunityId = useMemo(() => {
    if (!derivedCommunity || !communities.length) return null;
    const target = normalize(derivedCommunity);
    const found = communities.find((c) => normalize(c.name) === target);
    return found ? found.id : null;
  }, [derivedCommunity, communities]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    if (!form.date) { setApiError({ message: 'Fecha obligatoria.' }); return; }
    if (!form.province) { setApiError({ message: 'Provincia obligatoria para un festivo local.' }); return; }
    if (!form.locality.trim()) { setApiError({ message: 'Localidad obligatoria para un festivo local.' }); return; }
    if (!form.description.trim()) { setApiError({ message: 'Descripción obligatoria.' }); return; }
    if (derivedCommunityId == null) {
      setApiError({ message: 'No se pudo derivar la comunidad autónoma de esa provincia.' });
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        date: form.date, // fecha real del festivo local, sin traslado
        description: encodeDescription(form.description, form.province, form.locality),
        autonomousCommunity: derivedCommunityId,
      });
      onClose();
    } catch (err) {
      setApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Calendario</div>
            <h2 className="font-display text-lg font-bold text-ink">Nuevo festivo local</h2>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="text-xs text-ink-muted bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex items-start gap-2">
            <Info size={14} className="text-primary-darker mt-0.5 shrink-0" />
            <span>
              Los festivos nacionales y autonómicos ya están en el calendario. Aquí se dan de alta los festivos LOCALES de un municipio concreto.
              La comunidad autónoma se asigna automáticamente a partir de la provincia.
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Fecha</label>
              <input type="date" value={form.date} onChange={set('date')}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm" />
              {form.date && (
                <div className="text-[11px] mt-1 text-ink-muted">
                  Día seleccionado: {dayOfWeekEs(form.date)}.
                  {dayOfWeekEs(form.date) === 'domingo' && (
                    <> Se guarda en su fecha real: los festivos locales no se trasladan al lunes.</>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Provincia</label>
              <select value={form.province} onChange={(e) => { set('province')(e); setSuggestions([]); }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm">
                <option value="">Selecciona…</option>
                {PROVINCES.map((p) => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
              {derivedCommunity && (
                <div className="text-[11px] text-ink-muted mt-1">
                  Comunidad: <span className="font-semibold text-ink">{derivedCommunity}</span>
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">
              Localidad
              {!form.province && <span className="text-ink-muted/60 normal-case ml-1">(elige primero la provincia)</span>}
            </label>
            <div className="relative">
              <input
                ref={localityInputRef}
                type="text"
                value={form.locality}
                onChange={(e) => { set('locality')(e); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                disabled={!form.province}
                placeholder={form.province ? 'Ej. Sevilla, Carmona, Bilbao…' : ''}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm disabled:bg-slate-50"
                autoComplete="off"
              />
              {loadingSuggestions && form.locality.length >= 2 && (
                <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted animate-spin" />
              )}
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <ul className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <li key={`${s.name}-${i}`}>
                    <button
                      type="button"
                      onMouseDown={(ev) => ev.preventDefault()}
                      onClick={() => {
                        setForm((f) => ({ ...f, locality: s.name }));
                        setShowSuggestions(false);
                        setSuggestions([]);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                    >
                      <MapPin size={12} className="text-primary-darker shrink-0" />
                      <span className="font-semibold text-ink">{s.name}</span>
                      {s.province && <span className="text-xs text-ink-muted">· {s.province}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="text-[11px] text-ink-muted mt-1">
              Sugerencias en vivo. Puedes escribir directamente si tu municipio no aparece.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Descripción</label>
            <input type="text" value={form.description} onChange={set('description')}
              placeholder="Ej. Feria de la localidad"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm" />
          </div>

          {apiError && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2">{apiError.message}</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark disabled:opacity-60">
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Crear festivo local
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Página ────────────────────────────────────────────────────────────────
const FestivosPage = () => {
  const { user } = useAuth();
  const canManage = can(user?.type, 'manageHolidays');

  const [year, setYear] = useState(new Date().getFullYear());
  const [communities, setCommunities] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState('locales'); // 'all' | 'locales' | 'national' | id
  const [query, setQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [acRaw, list] = await Promise.all([
        listAutonomousCommunities().catch(() => []),
        getAllHolidaysForYear(year).catch(() => []),
      ]);
      setCommunities(Array.isArray(acRaw) ? acRaw : []);
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const idToName = useMemo(() => {
    const m = new Map();
    communities.forEach((c) => m.set(Number(c.id), c.name));
    return m;
  }, [communities]);

  const enriched = useMemo(() => items.map((h) => {
    const ac = Number(h.autonomousCommunity ?? h.AutonomousCommunity ?? -1);
    const raw = h.description ?? h.Description ?? '';
    const parsed = parseDescription(raw);
    return {
      id: h.id,
      date: String(h.date ?? h.Date ?? '').slice(0, 10),
      autonomousCommunity: ac,
      communityName: ac === 0 ? 'Nacional' : (idToName.get(ac) || `#${ac}`),
      description: parsed.description,
      province: parsed.province,
      locality: parsed.locality,
      isLocal: !!(parsed.province || parsed.locality),
    };
  }), [items, idToName]);

  const filtered = useMemo(() => {
    let arr = enriched;
    if (filter === 'locales') arr = arr.filter((h) => h.isLocal);
    else if (filter === 'national') arr = arr.filter((h) => h.autonomousCommunity === 0);
    else if (filter !== 'all') arr = arr.filter((h) => h.autonomousCommunity === Number(filter));
    if (query.trim()) {
      const q = query.toLowerCase();
      arr = arr.filter((h) =>
        (h.description || '').toLowerCase().includes(q) ||
        (h.province || '').toLowerCase().includes(q) ||
        (h.locality || '').toLowerCase().includes(q) ||
        (h.communityName || '').toLowerCase().includes(q)
      );
    }
    return arr.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }, [enriched, filter, query]);

  // Errores del alta: NewHolidayModal los enseña (sigue abierto).
  const handleCreate = async (payload) => {
    await addHoliday(payload);
    invalidateHolidayCache(year);
    toast.success(FEEDBACK.holidayCreated(payload.date));
    await load();
  };

  const handleDelete = async () => {
    const item = deleteDialog.item;
    if (!item) return;
    try {
      await deleteHoliday(item.id);
      invalidateHolidayCache(year);
      toast.success(FEEDBACK.holidayDeleted({ description: item.description, dayKey: item.date }));
      await load();
    } catch (err) {
      // Antes iba al banner de carga de la página; el diálogo se cierra, así
      // que el aviso tiene que viajar con el toast.
      toast.error(err, 'No se pudo eliminar el festivo.');
    } finally {
      setDeleteDialog({ open: false, item: null });
    }
  };

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const arr = [];
    for (let y = now - 2; y <= now + 2; y += 1) arr.push(y);
    return arr;
  }, []);

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Calendario</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">Festivos locales</h1>
          <div className="text-xs text-ink-muted mt-1">
            Los festivos nacionales y autonómicos ya están cargados en el calendario; aquí se gestionan los festivos LOCALES de cada municipio.
          </div>
        </div>
        {canManage && (
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors shrink-0"
          >
            <Plus size={16} /> Nuevo festivo local
          </button>
        )}
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{error.message}</div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Año</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm">
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Mostrar</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm">
            <option value="locales">Solo locales</option>
            <option value="all">Todos</option>
            <option value="national">Solo nacionales</option>
            {communities.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por descripción, provincia o localidad…"
            className="pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm w-full" />
        </div>
      </div>

      <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="py-10 flex items-center justify-center gap-2 text-ink-muted">
            <Loader2 size={18} className="animate-spin text-primary" />
            <span className="text-sm">Cargando festivos…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-2">
            <CalendarDays size={28} className="text-ink-muted/50" />
            <div className="text-sm text-ink-muted">
              {items.length === 0
                ? `No hay festivos guardados para ${year} todavía.`
                : 'Ningún festivo coincide con el filtro.'}
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-background">
              <tr className="text-xs uppercase tracking-wider text-ink-muted">
                <th className="text-left font-semibold py-3 pr-4 w-32">Fecha</th>
                <th className="text-left font-semibold py-3 pr-4">Descripción</th>
                <th className="text-left font-semibold py-3 pr-4 hidden md:table-cell">Ámbito</th>
                <th className="text-left font-semibold py-3 pr-4 hidden lg:table-cell">Provincia / Localidad</th>
                <th className="text-right font-semibold py-3 pr-4 w-16"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filtered.map((h) => (
                <tr key={h.id ?? `${h.date}-${h.description}`} className="border-t border-slate-100 hover:bg-white transition-colors">
                  <td className="py-2.5 pr-4 font-mono tabular-nums text-ink">{fmtDate(h.date)}</td>
                  <td className="py-2.5 pr-4">
                    <div className="font-semibold text-ink">{h.description}</div>
                  </td>
                  <td className="py-2.5 pr-4 hidden md:table-cell">
                    {h.autonomousCommunity === 0 ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-soft text-primary-darker">
                        <Globe2 size={11} /> Nacional
                      </span>
                    ) : h.isLocal ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-warning/15 text-warning">
                        Local · {h.communityName}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-light text-accent-dark">
                        {h.communityName}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-4 hidden lg:table-cell text-ink-muted">
                    {h.province || h.locality ? (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <MapPin size={11} />
                        {[h.province, h.locality].filter(Boolean).join(' · ')}
                      </span>
                    ) : <span className="text-xs text-ink-muted/60">—</span>}
                  </td>
                  <td className="py-2.5 pr-4 text-right">
                    {canManage && h.id != null && (
                      <button onClick={() => setDeleteDialog({ open: true, item: h })}
                        className="p-1.5 rounded text-ink-muted hover:text-danger hover:bg-slate-100 transition-colors"
                        title="Eliminar">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewHolidayModal
        open={modalOpen}
        communities={communities}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
      />

      <ConfirmDialog
        open={deleteDialog.open}
        title="Eliminar festivo"
        message={`¿Eliminar «${deleteDialog.item?.description}» del ${fmtDate(deleteDialog.item?.date)}?`}
        confirmText="Eliminar"
        onClose={() => setDeleteDialog({ open: false, item: null })}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default FestivosPage;
